import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const DisplaySettingsContext = createContext();

const STORAGE_KEY = 'safeguardpro_display_settings_v1';

const DEFAULT_SETTINGS = {
  textScale: 1,
  contrast: 'normal',
  reducedMotion: false,
  dyslexiaFont: false,
  theme: 'auto'
};

const readStoredSettings = () => {
  if (typeof window === 'undefined') return { ...DEFAULT_SETTINGS };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_SETTINGS, ...parsed };
  } catch (error) {
    return { ...DEFAULT_SETTINGS };
  }
};

const getPrefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches);
};

const prefersDarkScheme = () => {
  if (typeof window === 'undefined') return false;
  return Boolean(window.matchMedia?.('(prefers-color-scheme: dark)')?.matches);
};

const resolveTheme = (theme) => {
  if (theme === 'dark') return 'dark';
  if (theme === 'light') return 'light';
  return prefersDarkScheme() ? 'dark' : 'light';
};

const applySettings = (settings) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.style.setProperty('--sp-font-scale', settings.textScale);
  root.dataset.contrast = settings.contrast === 'high' ? 'high' : 'normal';
  root.dataset.motion = settings.reducedMotion ? 'reduce' : 'normal';
  root.dataset.font = settings.dyslexiaFont ? 'dyslexia' : 'default';
  root.dataset.theme = resolveTheme(settings.theme || 'auto');
};

export function DisplaySettingsProvider({ children }) {
  const [settings, setSettings] = useState(() => {
    const stored = readStoredSettings();
    if (stored.reducedMotion === false && getPrefersReducedMotion()) {
      return { ...stored, reducedMotion: true };
    }
    return stored;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    applySettings(settings);
  }, [settings]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if ((settings.theme || 'auto') !== 'auto') return undefined;
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return undefined;
    const handleChange = () => applySettings(settings);
    if (media.addEventListener) {
      media.addEventListener('change', handleChange);
      return () => media.removeEventListener('change', handleChange);
    }
    media.addListener(handleChange);
    return () => media.removeListener(handleChange);
  }, [settings]);

  const reset = () => setSettings({ ...DEFAULT_SETTINGS });

  const value = useMemo(
    () => ({
      settings,
      setSettings,
      reset
    }),
    [settings]
  );

  return <DisplaySettingsContext.Provider value={value}>{children}</DisplaySettingsContext.Provider>;
}

export function useDisplaySettings() {
  const context = useContext(DisplaySettingsContext);
  if (!context) {
    return { settings: { ...DEFAULT_SETTINGS }, setSettings: () => {}, reset: () => {} };
  }
  return context;
}
